/**
 * Global search data layer — the single typed wrapper around the
 * `global_search` Postgres RPC (migration 20260703010200).
 *
 * Pure and isomorphic (takes a Supabase client as a param, no next/headers), so
 * the same function backs the client overlay (TanStack Query) and the server
 * `/search` results page. RLS + the per-entity discovery predicates live in the
 * RPC, so this layer never restates a visibility gate (Critical Rule #8).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { decodeRankCursor, encodeRankCursor } from '@kit/shared/search';

import type { Database } from '~/lib/database.types';

export const GLOBAL_SEARCH_KINDS = [
  'dancer',
  'organization',
  'audition',
  'event',
] as const;

export type GlobalSearchKind = (typeof GLOBAL_SEARCH_KINDS)[number];

/**
 * Render order for grouped results (overlay + `/search` "All"): opportunities
 * first (competitions & auditions, then events), then people, then orgs — a
 * dancer opens the search to find something to apply to, so the actionable
 * surfaces lead (2026-07-05). Distinct from `GLOBAL_SEARCH_KINDS`, which is
 * the RPC/type-filter order and must stay stable.
 */
export const GLOBAL_SEARCH_DISPLAY_ORDER = [
  'audition',
  'event',
  'dancer',
  'organization',
] as const satisfies readonly GlobalSearchKind[];

/** One result row, mapped from the RPC's flat return shape. */
export interface GlobalSearchResult {
  kind: GlobalSearchKind;
  id: string;
  title: string;
  subtitle: string | null;
  /** dancer/event: image URL; organization: logo media uuid; audition: null. */
  imageRef: string | null;
  /**
   * handle (dancer/org), slug (audition), or id (event). NULLABLE for
   * dancer/org: legacy/incomplete profiles have no handle, so the RPC returns
   * null `link_slug` — callers must guard (see `resultHref`), or the link
   * resolves to `/null` and 404s (BAL-715).
   */
  linkSlug: string | null;
  isCompetition: boolean | null;
  rank: number;
  createdAt: string;
}

export interface GlobalSearchParams {
  query: string;
  /** Restrict to a subset; omit for all four (overlay). */
  types?: GlobalSearchKind[];
  /** Per-entity cap in overlay mode; page size in see-all mode. */
  limit?: number;
  /** Opaque rank cursor from a previous page (see-all mode only). */
  cursor?: string | null;
}

export interface GlobalSearchPage {
  results: GlobalSearchResult[];
  /** Grouped by kind for the overlay; every kind key is always present. */
  grouped: Record<GlobalSearchKind, GlobalSearchResult[]>;
  /** Next-page cursor (see-all single-type mode); null when no more rows. */
  nextCursor: string | null;
}

/** Min query length before the RPC is worth calling (matches the admin palette). */
export const GLOBAL_SEARCH_MIN_CHARS = 2;
/** Default per-entity result count in the overlay. */
export const GLOBAL_SEARCH_OVERLAY_LIMIT = 6;

function emptyGrouped(): Record<GlobalSearchKind, GlobalSearchResult[]> {
  return { dancer: [], organization: [], audition: [], event: [] };
}

/**
 * Run the unified global search.
 *
 * Overlay mode: pass no `types` (or a subset) and no `cursor` — each entity
 * returns its top `limit` by rank. See-all mode: pass a single `types` entry +
 * `cursor` to keyset-paginate that one entity; this over-fetches by one to
 * detect the next page and returns a `nextCursor`.
 */
export async function runGlobalSearch(
  client: SupabaseClient<Database>,
  params: GlobalSearchParams,
): Promise<GlobalSearchPage> {
  const query = params.query.trim();
  if (query.length < GLOBAL_SEARCH_MIN_CHARS) {
    return { results: [], grouped: emptyGrouped(), nextCursor: null };
  }

  const limit = params.limit ?? GLOBAL_SEARCH_OVERLAY_LIMIT;
  const singleType = params.types?.length === 1;
  const cursor = params.cursor ? decodeRankCursor(params.cursor) : null;

  const { data, error } = await client.rpc('global_search', {
    p_query: query,
    p_types: params.types ?? undefined,
    // Over-fetch by one ONLY in see-all mode, where a single entity paginates.
    p_limit: singleType ? limit + 1 : limit,
    p_cursor_rank: cursor?.rank ?? undefined,
    p_cursor_created_at: cursor?.createdAt ?? undefined,
    p_cursor_id: cursor?.id ?? undefined,
  });

  if (error) {
    throw error;
  }

  const rows = (data ?? []).map(
    (r): GlobalSearchResult => ({
      kind: r.kind as GlobalSearchKind,
      id: r.id,
      title: r.title,
      subtitle: r.subtitle,
      imageRef: r.image_ref,
      linkSlug: r.link_slug,
      isCompetition: r.is_competition,
      rank: r.rank,
      createdAt: r.created_at,
    }),
  );

  // See-all pagination: trim the over-fetched row and emit the next cursor.
  let pageRows = rows;
  let nextCursor: string | null = null;
  if (singleType && rows.length > limit) {
    pageRows = rows.slice(0, limit);
    const last = pageRows[pageRows.length - 1];
    if (last) {
      nextCursor = encodeRankCursor({
        rank: last.rank,
        createdAt: last.createdAt,
        id: last.id,
      });
    }
  }

  const grouped = emptyGrouped();
  for (const row of pageRows) {
    grouped[row.kind]?.push(row);
  }

  return { results: pageRows, grouped, nextCursor };
}
